import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CheckCheck } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Alarms({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [onlyOpen, setOnlyOpen] = useState(true);

  const load = async () => {
    const params = { limit: 200 };
    if (onlyOpen) params.acknowledged = false;
    const { data } = await api.get("/alarms", { params });
    setRows(data.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyOpen]);

  const ack = async (id) => {
    try {
      await api.post(`/alarms/${id}/ack`);
      toast.success("Alarm acknowledged");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const [ackingAll, setAckingAll] = useState(false);
  const openCount = rows.filter((a) => !a.acknowledged).length;
  const ackAll = async () => {
    setAckingAll(true);
    try {
      const { data } = await api.post("/alarms/ack-all");
      toast.success(`${data.acknowledged} alarm${data.acknowledged === 1 ? "" : "s"} acknowledged`);
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setAckingAll(false); }
  };

  return (
    <div className="space-y-6" data-testid="alarms-page">
      {!embedded && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Safety</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Alarms</h1>
          <p className="text-sm text-slate-500 mt-1">Faults and safety events reported by meters.</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant={onlyOpen ? "default" : "outline"}
              onClick={() => setOnlyOpen(true)}
              data-testid="alarms-filter-open"
              className={onlyOpen ? "bg-blue-600 hover:bg-blue-700" : ""}>
              Open
            </Button>
            <Button variant={onlyOpen ? "outline" : "default"}
              onClick={() => setOnlyOpen(false)}
              data-testid="alarms-filter-all"
              className={!onlyOpen ? "bg-blue-600 hover:bg-blue-700" : ""}>
              All
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-slate-500 tabular-nums">{rows.length} shown</div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={ackingAll || openCount === 0} data-testid="alarms-ack-all">
                  <CheckCheck size={14} className="mr-1" /> {ackingAll ? "Acknowledging…" : "Acknowledge all"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="alarms-ack-all-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Acknowledge all open alarms?</AlertDialogTitle>
                  <AlertDialogDescription>Every unacknowledged alarm across the fleet will be marked as acknowledged.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="alarms-ack-all-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={ackAll} data-testid="alarms-ack-all-confirm">Acknowledge all</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <div>
          {rows.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-400">Nothing to worry about — no alarms.</div>
          )}
          {rows.map((a) => (
            <div key={a.id} className="px-5 py-3 border-t border-slate-100 flex items-center justify-between" data-testid={`alarm-row-${a.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <StatusPill value={a.severity} />
                  <span className="text-sm font-medium text-slate-800">{a.code.replace(/_/g, " ")}</span>
                  {a.acknowledged && <span className="text-[10px] uppercase text-slate-400 tracking-wider">ACK</span>}
                </div>
                <div className="text-xs text-slate-500 font-mono mt-1">{new Date(a.created_at).toLocaleString()}</div>
                {a.message && <div className="text-xs text-slate-600 mt-1">{a.message}</div>}
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/meters/${a.meter_id}`} className="text-xs text-blue-600 hover:underline font-mono">meter {a.meter_id.slice(0, 8)}</Link>
                {!a.acknowledged && (
                  <Button size="sm" variant="outline" onClick={() => ack(a.id)} data-testid={`alarm-ack-${a.id}`}>
                    <Check size={14} className="mr-1" /> Ack
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
